import {
  faAngleDown,
  faAngleLeft,
  faAngleRight,
  faArrowRotateLeft,
  faBookmark,
  faCalendarPlus,
  faEllipsisVertical,
  faGear,
  faPlus,
  faPrint,
} from '@fortawesome/pro-regular-svg-icons';
import { EventDropArg } from '@fullcalendar/core';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Calendar } from 'src/components/Calendar/Calendar';
import { useCalendar } from 'src/components/Calendar/useCalendar';
import { Checkbox } from 'src/components/Checkbox';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { IconDatePicker } from 'src/components/IconDatePicker';
import { ListItem } from 'src/components/ListItem';
import { Meta } from 'src/components/Meta';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useMediaQuery, useTheme } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import {
  fetchAppointment,
  fetchAppointmentWaitListEntries,
  rescheduleAppointment,
} from 'src/models/appointment/appointment';
import { ScheduleItem } from 'src/models/schedule/_types';
import {
  ScheduledAppointment,
  fetchScheduledAppointment,
} from 'src/models/scheduled-appointment/scheduled-appointment';
import { updateUser } from 'src/models/user/user';
import { ScheduledMembership } from 'src/models/visit/_types';
import { fetchScheduledMemberships, fetchVisits } from 'src/models/visit/visit';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { ContainerBreakout } from 'src/shared/AppFrame/ContainerBreakout';
import { CheckboxFilter } from 'src/shared/Filters/CheckboxFilter';
import { FbmResourcesFilter } from 'src/shared/Filters/FbmResourcesFilter';
import { Filters, TagsFilter, TrainerFilter } from 'src/shared/Filters/Filters';
import { LocationFilter } from 'src/shared/Filters/LocationFilter';
import { NumberFilter } from 'src/shared/Filters/NumberFilter';
import { QuickLinks } from 'src/shared/Filters/QuickLinks';
import { ServiceCategoryFilter } from 'src/shared/Filters/ServiceCategoryFilter';
import { ServiceFilter } from 'src/shared/Filters/ServiceFilter';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { CalendarPopoverContent } from './CalendarPopoverContent';
import { NewEventDialog } from './NewEventDialog';
import { UnavailableDialog } from './UnavailableDialog';
import { ViewEventDialog } from './ViewEventDialog';

import type { Appointment } from 'src/models/appointment/_types';

export default function CalendarPage(): React.ReactNode {
  const { t } = useTranslation('translation', {
    keyPrefix: 'schedules.calendar',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { user, session } = useSession();

  const viewLabels = {
    timeGridWeek: t('views.weekView'),
    resourceTimeGridDay: t('views.dayView'),
    listWeek: t('views.listView'),
  };
  const queryClient = useQueryClient();
  const initialLoadRef = useRef(false);
  const [showIcalLink, setShowIcalLink] = useState(false);

  const {
    params,
    filterParams,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
    updateFromQuickLink,
    getQuickLinkString,
    searchParams,
  } = useListSearchParams();

  // Handle date changes from calendar - sync to URL
  const handleDateChange = (start: number, end: number) => {
    updateParam('start', String(start));
    updateParam('end', String(end));
  };

  const {
    next,
    prev,
    setView,
    setDateToToday,
    goToDate,
    view,
    title,
    formattedDate,
    calendarRef,
    onClickScheduledAppointment,
    onClickAppointment,
    onClickSchedule,
    calendarEvents,
    resources,
    isLoading,
    normalizedFilters,
    firstDay,
    scrollTime,
    slotDuration,
    hasTrainerTagFilter,
    date,
  } = useCalendar({
    cancelled: filterParams.cancelled,
    locationId: filterParams.locationId,
    noAvailabilitySchedules: filterParams.noAvailabilitySchedules,
    trainerId: filterParams.trainerId,
    noEmptyTrainer: filterParams.noEmptyTrainer,
    onlyAssets: filterParams.onlyAssets,
    spotsRemaining: filterParams.spotsRemaining,
    serviceId: filterParams.serviceId,
    category: filterParams.category,
    resourceId: filterParams.resourceId,
    trainerTag: filterParams.trainerTag,
    showAttendees: filterParams.showAttendees,
    start: searchParams.get('start') || undefined,
    end: searchParams.get('end') || undefined,
    onDateChange: handleDateChange,
  });

  // Apply default filters on first load if no filters are in URL
  useEffect(() => {
    if (initialLoadRef.current) return;

    const hasFilters = Object.keys(filterParams || {}).length > 0;
    if (!hasFilters) {
      initialLoadRef.current = true;
      if (user.defaultCalendarFilters) {
        // Convert defaultCalendarFilters to the format expected by updateFromQuickLink
        const defaultFilters = user.defaultCalendarFilters;
        Object.entries(defaultFilters).forEach(([key, value]) => {
          // Treat trainerId of "0" or any "false" value as blank (no filter)
          if (key === 'trainerId' && String(value) === '0') {
            return;
          }
          if (String(value) === 'false') {
            return;
          }
          if (value) {
            updateFilterParam(key, value);
          }
        });
      } else {
        // No saved default filters - default trainerId to current user
        updateFilterParam('trainerId', String(user.id));
      }
    } else {
      initialLoadRef.current = true;
    }
  }, [filterParams, user.defaultCalendarFilters, user.id, updateFilterParam]);

  // Get current filters as an object for comparison (using normalized values from hook)
  // Convert to strings for storage in defaultCalendarFilters
  const getCurrentFilters = () => ({
    trainerId: normalizedFilters.trainerId
      ? String(normalizedFilters.trainerId)
      : undefined,
    locationId: normalizedFilters.locationId
      ? String(normalizedFilters.locationId)
      : undefined,
    serviceId: normalizedFilters.serviceId
      ? String(normalizedFilters.serviceId)
      : undefined,
    category: normalizedFilters.category
      ? String(normalizedFilters.category)
      : undefined,
    resourceId: normalizedFilters.resourceId
      ? String(normalizedFilters.resourceId)
      : undefined,
    trainerTag: normalizedFilters.trainerTag
      ? String(normalizedFilters.trainerTag)
      : undefined,
    cancelled: normalizedFilters.cancelled
      ? String(normalizedFilters.cancelled)
      : undefined,
    noEmptyTrainer: normalizedFilters.noEmptyTrainer
      ? String(normalizedFilters.noEmptyTrainer)
      : undefined,
    noAvailabilitySchedules: normalizedFilters.noAvailabilitySchedules
      ? String(normalizedFilters.noAvailabilitySchedules)
      : undefined,
    showAttendees: normalizedFilters.showAttendees
      ? String(normalizedFilters.showAttendees)
      : undefined,
    onlyAssets: normalizedFilters.onlyAssets
      ? String(normalizedFilters.onlyAssets)
      : undefined,
    spotsRemaining: normalizedFilters.spotsRemaining
      ? String(normalizedFilters.spotsRemaining)
      : undefined,
  });

  // Check if current filters match default filters
  const filtersMatchDefault = () => {
    const current = getCurrentFilters();
    const defaults = user.defaultCalendarFilters || {};

    // Check each key in current filters
    for (const key of Object.keys(current)) {
      const currentValue = current[key] || undefined;
      const defaultValue = defaults[key] || undefined;
      if (currentValue !== defaultValue) {
        return false;
      }
    }

    // Check each key in default filters
    for (const key of Object.keys(defaults)) {
      const currentValue = current[key] || undefined;
      const defaultValue = defaults[key] || undefined;
      if (currentValue !== defaultValue) {
        return false;
      }
    }

    return true;
  };

  // Mutation to save default filters
  const setDefaultFiltersMutation = useMutation({
    mutationFn: async () => {
      return updateUser(user.id, {
        defaultCalendarFilters: getCurrentFilters(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      toast.success(t('settings.defaultFiltersSaved'));
    },
    onError: () => {
      toast.error(t('settings.defaultFiltersSaveFailed'));
    },
  });

  // Mutation to switch back to old calendar
  const switchToOldCalendarMutation = useMutation({
    mutationFn: async () => {
      return updateUser(user.id, {
        newCalendar: false,
      });
    },
    onSuccess: () => {
      // Redirect to the old Ember calendar
      window.location.href = '/ex4/embed/fbm/schedule/';
    },
    onError: () => {
      toast.error(t('settings.switchFailed'));
    },
  });

  // Generate iCal link
  const icalLink = `https://${window.location.host}/calendars?calendar=${user.hashid}`;

  // Copy iCal link to clipboard
  const copyIcalLink = async () => {
    try {
      await navigator.clipboard.writeText(icalLink);
      toast.success(t('settings.icalCopied'));
    } catch {
      toast.error(t('settings.icalCopyFailed'));
      setShowIcalLink(true);
    }
  };

  // Generate print URL with current filters
  const getPrintUrl = () => {
    const queryParams = new URLSearchParams();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    queryParams.set('time_zone', timeZone);

    if (searchParams.get('start')) {
      queryParams.set('start', searchParams.get('start') || '');
    }
    if (searchParams.get('end')) {
      queryParams.set('end', searchParams.get('end') || '');
    }
    if (filterParams.locationId) {
      queryParams.set('q[location]', String(filterParams.locationId));
    }
    if (filterParams.trainerId) {
      queryParams.set('user_id', String(filterParams.trainerId));
    }
    if (filterParams.serviceId) {
      queryParams.set('q[service_id]', String(filterParams.serviceId));
    }
    if (filterParams.category) {
      queryParams.set('q[category]', String(filterParams.category));
    }
    if (filterParams.cancelled) {
      queryParams.set('q[cancelled]', 'true');
    }
    if (filterParams.noAvailabilitySchedules) {
      queryParams.set('q[noAvailabilitySchedules]', 'true');
    }
    if (filterParams.showAttendees) {
      queryParams.set('q[showAttendees]', 'true');
    }
    if (filterParams.spotsRemaining) {
      queryParams.set(
        'q[spots_remaining]',
        String(filterParams.spotsRemaining)
      );
    }
    if (filterParams.resourceId) {
      queryParams.set('q[resource]', String(filterParams.resourceId));
    }
    if (filterParams.trainerTag) {
      queryParams.set('q[trainer_tag]', String(filterParams.trainerTag));
    }

    return `/api/v4/calendar/print/?${queryParams.toString()}`;
  };

  // Handle print schedule
  const handlePrintSchedule = () => {
    window.open(getPrintUrl(), '_blank');
  };

  // Apply quick link
  const applyQuickLink = (quickLink: {
    params?: Record<string, unknown>;
    filters?: string;
  }) => {
    if (quickLink.params) {
      // Filter out trainerId of "0" and any "false" values (treat as blank)
      const filteredParams: Record<string, unknown> = {};
      Object.entries(quickLink.params).forEach(([key, value]) => {
        if (String(value) === 'false') return;
        if (key === 'trainerId' && (value === '0' || value === 0)) return;
        filteredParams[key] = value;
      });
      updateFromQuickLink(filteredParams);
    }
  };

  // Selected date for new event dialog. Null means closed.
  const [newEventDialogDate, setNewEventDialogDate] = useState<Date | null>(
    null
  );
  const [newEventTrainerId, setNewEventTrainerId] = useState<
    number | undefined
  >(undefined);
  // Availability schedule to edit (when clicking on an availability event)
  const [newEventAvailabilitySchedule, setNewEventAvailabilitySchedule] =
    useState<ScheduleItem | null>(null);

  // Appointment for ViewEventDialog. Null means closed.
  // Can come from calendar click or after creating a new session.
  const [viewedAppointment, setViewedAppointment] =
    useState<Appointment | null>(null);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(false);

  // Schedule for ViewUnavailableDialog. Null means closed.
  const [viewedSchedule, setViewedSchedule] = useState<ScheduleItem | null>(
    null
  );
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);

  // Track if we've already tried to open event from URL
  const eventFromUrlRef = useRef(false);

  // Guard against dateClick and eventClick both firing on the same click.
  // FullCalendar can fire both after visibility transition from hidden to visible.
  const eventClickFiredRef = useRef(false);

  // State for reschedule confirmation dialog (3 options: notify, don't notify, cancel)
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const rescheduleResolveRef = useRef<((value: boolean | null) => void) | null>(
    null
  );

  // Show reschedule confirmation dialog and return: true (notify), false (don't notify), null (cancel)
  const confirmReschedule = (): Promise<boolean | null> => {
    return new Promise((resolve) => {
      rescheduleResolveRef.current = resolve;
      setRescheduleDialogOpen(true);
    });
  };

  const handleRescheduleNotify = () => {
    setRescheduleDialogOpen(false);
    rescheduleResolveRef.current?.(true);
  };

  const handleRescheduleDontNotify = () => {
    setRescheduleDialogOpen(false);
    rescheduleResolveRef.current?.(false);
  };

  const handleRescheduleCancel = () => {
    setRescheduleDialogOpen(false);
    rescheduleResolveRef.current?.(null);
  };

  // Mutation for rescheduling appointments via drag/drop
  const rescheduleAppointmentMutation = useMutation({
    mutationFn: rescheduleAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success(t('dragDrop.rescheduled'));
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.error(api.formatError(error, t('dragDrop.rescheduleFailed')));
    },
  });

  // Handle event drop (drag to new time/date)
  const handleEventDrop = async (info: EventDropArg) => {
    const event = info.event;
    const objectType = event.extendedProps.objectType;
    const objectId = event.extendedProps.objectId;

    // Only appointments are draggable
    if (objectType !== 'appointment') {
      info.revert();
      return;
    }

    const newStart = event.start;
    const newEnd = event.end;

    if (!newStart || !newEnd) {
      info.revert();
      return;
    }

    // Show confirmation dialog: true = notify, false = don't notify, null = cancel
    const result = await confirmReschedule();

    if (result === null) {
      info.revert();
      return;
    }

    // Proceed with reschedule
    try {
      await rescheduleAppointmentMutation.mutateAsync({
        appointmentId: objectId,
        startTime: newStart,
        endTime: newEnd,
        disableEmail: !result,
      });
    } catch {
      info.revert();
    }
  };

  // Handle event resize (change duration)
  const handleEventResize = async (info: EventResizeDoneArg) => {
    const event = info.event;
    const objectType = event.extendedProps.objectType;
    const objectId = event.extendedProps.objectId;

    // Only appointments are resizable
    if (objectType !== 'appointment') {
      info.revert();
      return;
    }

    const newStart = event.start;
    const newEnd = event.end;

    if (!newStart || !newEnd) {
      info.revert();
      return;
    }

    // Show confirmation dialog: true = notify, false = don't notify, null = cancel
    const result = await confirmReschedule();

    if (result === null) {
      info.revert();
      return;
    }

    // Proceed with reschedule
    try {
      await rescheduleAppointmentMutation.mutateAsync({
        appointmentId: objectId,
        startTime: newStart,
        endTime: newEnd,
        disableEmail: !result,
      });
    } catch {
      info.revert();
    }
  };

  // Get eventId and eventType from URL
  const eventIdFromUrl = searchParams.get('eventId');
  const eventTypeFromUrl = searchParams.get('eventType');

  // Update URL when opening/closing event dialog
  const setEventInUrl = (
    objectId: string | number | null,
    objectType: string | null
  ) => {
    if (objectId && objectType) {
      updateParam('eventId', String(objectId));
      updateParam('eventType', objectType);
    } else {
      updateParam('eventId', null);
      updateParam('eventType', null);
    }
  };

  // Open event from URL on page load
  useEffect(() => {
    if (eventFromUrlRef.current) return;
    eventFromUrlRef.current = true;

    if (!eventIdFromUrl || !eventTypeFromUrl) return;

    // Open the event dialog based on type - fetch directly by ID
    const openEventFromUrl = async () => {
      try {
        if (eventTypeFromUrl === 'schedule') {
          setIsLoadingSchedule(true);
          const schedule = await import('src/models/schedule/schedule').then(
            (m) => m.fetchSchedule(eventIdFromUrl)
          );
          setViewedSchedule(schedule);
        } else if (
          eventTypeFromUrl === 'appointment' ||
          eventTypeFromUrl === 'scheduled_appointment'
        ) {
          setIsLoadingAppointment(true);
          const appointment = await fetchAppointment(eventIdFromUrl);

          // Fetch visits and waitlist entries
          const [visitsResponse, entries] = await Promise.all([
            fetchVisits({
              sortKeys: ['status asc', 'user_first_name asc'],
              filters: { appointmentId: appointment.id, allVisits: true },
              includeClients: true,
              perPage: 100,
            }),
            fetchAppointmentWaitListEntries(appointment.id),
          ]);

          // If it's a recurring session, also fetch scheduled appointment and members
          let scheduledAppointment: ScheduledAppointment | null | undefined =
            undefined;
          let scheduledMembers: ScheduledMembership[] | null | undefined =
            undefined;

          if (appointment.scheduledAppointmentId) {
            const [scheduledAppt, membersResponse] = await Promise.all([
              fetchScheduledAppointment(appointment.scheduledAppointmentId),
              fetchScheduledMemberships({
                scheduledAppointmentId: appointment.scheduledAppointmentId,
                appointmentId: appointment.id,
                fetchAll: true,
              }),
            ]);
            scheduledAppointment = scheduledAppt;
            scheduledMembers = membersResponse.data;
          }

          setViewedAppointment({
            ...appointment,
            visits: visitsResponse.visits,
            entries,
            scheduledAppointment,
            scheduledMembers,
          });
        }
      } catch {
        // Error handled by API layer - clear URL params on error
        updateParam('eventId', null);
        updateParam('eventType', null);
      } finally {
        setIsLoadingAppointment(false);
        setIsLoadingSchedule(false);
      }
    };

    openEventFromUrl();
  }, [eventIdFromUrl, eventTypeFromUrl, updateParam]);

  const setDefaultViewMutation = useMutation({
    mutationFn: async () => {
      return updateUser(user.id, {
        defaultCalendarView: view,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      toast.success(t('settings.defaultViewSaved'));
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('settings.defaultViewSaveFailed')));
    },
  });

  const filters = [
    {
      field: 'locationId',
      label: t('filters.location'),
      Component: LocationFilter,
      props: { multiple: true },
    },
    {
      field: 'serviceId',
      label: t('filters.service'),
      Component: ServiceFilter,
      props: { multiple: true },
    },
    {
      field: 'category',
      label: t('filters.serviceCategory'),
      Component: ServiceCategoryFilter,
    },
    {
      field: 'resourceId',
      label: t('filters.bookedAsset'),
      Component: FbmResourcesFilter,
      props: { multiple: true },
    },
    // Staff filter is hidden when Staff Tags filter is active (day view only)
    ...(!hasTrainerTagFilter
      ? [
          {
            field: 'trainerId',
            label: t('filters.staff'),
            Component: TrainerFilter,
            props: { multiple: true },
          },
        ]
      : []),
    // Staff Tags filter is only available in day view
    ...(view === 'resourceTimeGridDay'
      ? [
          {
            field: 'trainerTag',
            label: t('filters.staffTags'),
            Component: TagsFilter,
            description: t('filters.staffTagsDescription'),
            props: { type: 'trainer', showMatchAll: false },
          },
        ]
      : []),
    {
      field: 'cancelled',
      label: t('filters.showCancelled'),
      Component: CheckboxFilter,
    },
    {
      field: 'noAvailabilitySchedules',
      label: t('filters.hideAvailabilitySchedules'),
      Component: CheckboxFilter,
    },
    {
      field: 'spotsRemaining',
      label: t('filters.spotsRemaining'),
      Component: NumberFilter,
    },
  ];

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const theme = useTheme();

  useEffect(() => {
    if (!isLoading) {
      // scrollIntoView uses the edges of the screen, so we need to account for the header height
      // Scrolling to 3AM gets us to roughly 7AM depending on breakpoint
      const elem = document.querySelector('[data-time="03:00:00"]');
      if (elem) {
        elem.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    }
  }, [isLoading]);

  return (
    <>
      <Meta title={t('title')} brand={session?.brand} />
      <AppFrameConfig
        id="client"
        containerMaxWidth={false}
        title={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{title || formattedDate}</span>
            <IconDatePicker
              value={date}
              onChange={(newDate) => {
                if (newDate) {
                  goToDate(newDate);
                }
              }}
            />
            <IconButton
              onClick={() => setNewEventDialogDate(date)}
              sx={{ p: 0.5 }}
            >
              <Icon icon={faPlus} />
            </IconButton>
          </Stack>
        }
        fixedHeader={true}
        row={
          <Scroller
            direction="row"
            spacing={2}
            hideScrollbar
            sx={{
              ml: -2,
              px: 2,
              pb: { xs: 1, sm: 2 },
              boxSizing: 'content-box',
            }}
          >
            <QuickLinks
              type="schedule"
              linkerId={user.id}
              onApply={applyQuickLink}
              params={params}
              filterParams={filterParams}
              getQuickLinkString={getQuickLinkString}
              showDefaultButton={false}
            />
            <Filters
              filters={filters}
              params={params}
              updateParam={updateParam}
              updateFilterParam={updateFilterParam}
              getFilterParamValue={getFilterParamValue}
              resetFilterParams={resetFilterParams}
            />
          </Scroller>
        }
        actions={
          <>
            <Stack direction="row">
              <DropdownMenu
                trigger={
                  smDown ? (
                    <IconButton size={smDown ? 'small' : 'medium'}>
                      <Icon icon={faEllipsisVertical} />
                    </IconButton>
                  ) : (
                    <Button color="neutral">
                      {viewLabels[view]}{' '}
                      <Icon size="xsmall" icon={faAngleDown} sx={{ ml: 0.5 }} />
                    </Button>
                  )
                }
              >
                <Stack spacing={1} sx={{ p: 1 }}>
                  <ListItem
                    selected={view === 'resourceTimeGridDay'}
                    primary={t('views.dayViewAllStaff')}
                    clickable
                    rounded
                    onClick={() => setView('resourceTimeGridDay')}
                  />
                  <ListItem
                    clickable
                    rounded
                    selected={view === 'timeGridWeek'}
                    primary={viewLabels['timeGridWeek']}
                    onClick={() => setView('timeGridWeek')}
                  />
                  <ListItem
                    clickable
                    rounded
                    selected={view === 'listWeek'}
                    primary={viewLabels['listWeek']}
                    onClick={() => setView('listWeek')}
                  />
                  <Divider />
                  <ListItem
                    clickable
                    rounded
                    primary={t('views.setDefault')}
                    secondary={t('views.setDefaultDescription')}
                    color="error"
                    disabled={
                      setDefaultViewMutation.isPending ||
                      view === user?.defaultCalendarView
                    }
                    onClick={() => setDefaultViewMutation.mutate()}
                  />
                </Stack>
              </DropdownMenu>
            </Stack>

            {!smDown && (
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={prev} size={smDown ? 'small' : 'medium'}>
                  <Icon icon={faAngleLeft} />
                </IconButton>
                <Button
                  onClick={setDateToToday}
                  color="neutral"
                  size={smDown ? 'small' : 'medium'}
                >
                  {t('navigation.today')}
                </Button>
                <IconButton onClick={next} size={smDown ? 'small' : 'medium'}>
                  <Icon icon={faAngleRight} />
                </IconButton>
              </Stack>
            )}

            <DropdownMenu
              closeOnContentClick={false}
              trigger={
                <IconButton size={smDown ? 'small' : 'medium'}>
                  <Icon icon={faGear} />
                </IconButton>
              }
            >
              <Stack spacing={1} sx={{ p: 1 }}>
                <ListItem
                  clickable
                  rounded
                  primary={t('settings.printSchedule')}
                  icon={faPrint}
                  onClick={handlePrintSchedule}
                />
                {!filtersMatchDefault() && (
                  <ListItem
                    clickable
                    rounded
                    primary={t('settings.makeDefaultFilters')}
                    secondary={t('settings.makeDefaultFiltersDescription')}
                    icon={faBookmark}
                    disabled={setDefaultFiltersMutation.isPending}
                    onClick={() => setDefaultFiltersMutation.mutate()}
                  />
                )}
                <ListItem
                  clickable
                  rounded
                  primary={t('settings.addToCalendarApp')}
                  secondary={t('settings.addToCalendarAppDescription')}
                  icon={faCalendarPlus}
                  onClick={copyIcalLink}
                />
                {showIcalLink && (
                  <Box sx={{ px: 2 }}>
                    <TextField
                      label={t('settings.icalLink')}
                      value={icalLink}
                      disabled={true}
                    />
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <ListItem
                  clickable
                  rounded
                  primary={t('settings.switchToOldCalendar')}
                  icon={faArrowRotateLeft}
                  disabled={switchToOldCalendarMutation.isPending}
                  onClick={() => switchToOldCalendarMutation.mutate()}
                />
                <Divider sx={{ my: 1 }} />
                <Typography
                  color="textSecondary"
                  variant="body2"
                  sx={{ px: 1 }}
                >
                  {t('settings.viewOptions')}
                </Typography>
                <Box sx={{ px: 1, pb: 1 }}>
                  <Stack spacing={2}>
                    <Checkbox
                      onChange={(e) =>
                        updateFilterParam('showAttendees', e.target.checked)
                      }
                      checked={filterParams.showAttendees || false}
                      label={t('settings.showAttendeesOnListAndPrint')}
                    />
                    {view === 'resourceTimeGridDay' && (
                      <>
                        <Checkbox
                          onChange={(e) =>
                            updateFilterParam('onlyAssets', e.target.checked)
                          }
                          checked={filterParams.onlyAssets || false}
                          label={t('settings.showOnlyAssetColumns')}
                        />
                        <Checkbox
                          onChange={(e) =>
                            updateFilterParam(
                              'noEmptyTrainer',
                              e.target.checked
                            )
                          }
                          checked={filterParams.noEmptyTrainer || false}
                          label={t('settings.hideEmptyColumns')}
                        />
                      </>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </DropdownMenu>
          </>
        }
      />
      <Box sx={{ mt: { xs: 2, sm: 0 }, position: 'relative' }}>
        {isLoading && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              minHeight: '50svh',
              top: '0px',
              left: '0px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `rgba(${theme.vars.palette.background.defaultChannel} / 0.7)`,
              zIndex: 100,
            }}
          >
            <CircularProgress size="large" />
            <Typography color="textSecondary" sx={{ mt: 3 }}>
              {t('loading.loadingEvents')}
            </Typography>
          </Box>
        )}
        <ContainerBreakout smDown>
          {/* Keep calendar in layout (visibility) so FullCalendar gets proper dimensions from the start.
              display:none would give it zero dimensions and cause sizing issues when it appears. */}
          <Box
            sx={{
              visibility: isLoading ? 'hidden' : 'visible',
              pointerEvents: isLoading ? 'none' : 'auto',
            }}
          >
            <Calendar
              initialMode={
                (user?.defaultCalendarView as 'timeGridWeek' | 'listWeek') ||
                'timeGridWeek'
              }
              initialDate={date}
              PopoverContent={CalendarPopoverContent}
              events={calendarEvents}
              onDateClick={async (info) => {
                if (eventClickFiredRef.current) return;

                // Extract trainer ID from resource (format: "user_123")
                const resourceId = info.resource?.id;
                const trainerId =
                  typeof resourceId === 'string' &&
                  resourceId.startsWith('user_')
                    ? parseInt(resourceId.split('_')[1], 10)
                    : undefined;
                setNewEventTrainerId(trainerId);
                setNewEventDialogDate(info.date);
                setNewEventAvailabilitySchedule(null);
              }}
              onEventClick={async (info) => {
                eventClickFiredRef.current = true;
                setTimeout(() => { eventClickFiredRef.current = false; }, 0);

                const objectType = info.event.extendedProps.objectType;
                const objectId = info.event.extendedProps.objectId;

                if (!objectId) {
                  return;
                }

                // Store event in URL for sharing/reloading
                setEventInUrl(objectId, objectType);

                try {
                  if (
                    objectType === 'scheduled_appointment' ||
                    objectType === 'appointment'
                  ) {
                    setIsLoadingAppointment(true);

                    let appointment: Appointment;
                    let scheduledAppointment:
                      | ScheduledAppointment
                      | undefined
                      | null = undefined;
                    let scheduledMembers:
                      | ScheduledMembership[]
                      | null
                      | undefined = undefined;

                    if (objectType === 'scheduled_appointment') {
                      ({ appointment, scheduledAppointment, scheduledMembers } =
                        await onClickScheduledAppointment(info));
                    } else {
                      ({ appointment, scheduledAppointment, scheduledMembers } =
                        await onClickAppointment(info));
                    }

                    // Fetch visits and waitlist entries for the appointment
                    const [visitsResponse, entries] = await Promise.all([
                      fetchVisits({
                        sortKeys: ['status asc', 'user_first_name asc'],
                        filters: {
                          appointmentId: appointment.id,
                          allVisits: true,
                        },
                        includeClients: true,
                        perPage: 100,
                      }),
                      fetchAppointmentWaitListEntries(appointment.id),
                    ]);

                    // Update URL with actual appointment ID (in case it was a scheduled_appointment)
                    setEventInUrl(appointment.id, 'appointment');

                    setViewedAppointment({
                      ...appointment,
                      visits: visitsResponse.visits,
                      entries,
                      scheduledAppointment,
                      scheduledMembers,
                    });
                  } else if (objectType === 'schedule') {
                    // Check if this is an availability schedule
                    const isAvailabilitySchedule =
                      info.event.extendedProps.data?.type === 'available';

                    if (isAvailabilitySchedule) {
                      // For availability schedules, open NewEventDialog with schedule info
                      // Don't use isLoadingSchedule as that would flash the UnavailableDialog
                      const { schedule, eventStartTime } =
                        await onClickSchedule(info);

                      // Open NewEventDialog with the availability schedule
                      setNewEventDialogDate(eventStartTime);
                      setNewEventAvailabilitySchedule(schedule);
                      // Extract trainer ID from resource if available
                      const resourceId = info.event.getResources()?.[0]?.id;
                      const eventTrainerId =
                        typeof resourceId === 'string' &&
                        resourceId.startsWith('user_')
                          ? parseInt(resourceId.split('_')[1], 10)
                          : undefined;
                      setNewEventTrainerId(eventTrainerId);
                      // Clear URL params since we're not viewing an event directly
                      setEventInUrl(null, null);
                    } else {
                      // For unavailable schedules, open the UnavailableDialog
                      setIsLoadingSchedule(true);
                      const { schedule } = await onClickSchedule(info);
                      setViewedSchedule(schedule);
                    }
                  }
                } catch {
                  // Error handled by API layer
                  setEventInUrl(null, null);
                } finally {
                  setIsLoadingAppointment(false);
                  setIsLoadingSchedule(false);
                }
              }}
              resources={resources}
              ref={calendarRef}
              firstDay={firstDay}
              scrollTime={scrollTime}
              slotDuration={slotDuration}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
              listEventContent={(e) => {
                const eventObj = e.event.extendedProps;
                return (
                  <Stack direction="column">
                    <div>{eventObj.text}</div>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mt: 0.5 }}
                    >
                      {(eventObj.data?.primaryTrainerName ||
                        eventObj.data?.trainerName) && (
                        <Typography variant="caption" color="textSecondary">
                          {t('filters.staff')}:{' '}
                          <Typography variant="caption" color="textPrimary">
                            {eventObj.data?.trainerName}
                          </Typography>
                        </Typography>
                      )}

                      {eventObj.data.location && (
                        <Typography variant="caption" color="textSecondary">
                          {t('filters.location')}:{' '}
                          <Typography variant="caption" color="textPrimary">
                            {eventObj.data?.location}
                          </Typography>
                        </Typography>
                      )}

                      {eventObj.data.visitsRemaining !== undefined && (
                        <Typography variant="caption" color="textSecondary">
                          {t('filters.spotsRemaining')}:{' '}
                          <Typography variant="caption" color="textPrimary">
                            {eventObj.data.visitsRemaining === null
                              ? tCommon('unlimited')
                              : eventObj.data.visitsRemaining}
                          </Typography>
                        </Typography>
                      )}

                      {filterParams.showAttendees &&
                        eventObj.data.clientName && (
                          <Typography variant="caption" color="textSecondary">
                            {tCommon('attendees')}:{' '}
                            <Typography variant="caption" color="textPrimary">
                              {eventObj.data.clientName}
                            </Typography>
                          </Typography>
                        )}
                    </Stack>
                  </Stack>
                );
              }}
            />
          </Box>
        </ContainerBreakout>
      </Box>
      {smDown && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '20px',
            left: '0px',
            width: '100vw',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Stack
            spacing={1}
            direction="row"
            sx={{
              minWidth: '100px',
              bgcolor: 'background.level2',
              color: 'neutral.50',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
            }}
          >
            <IconButton onClick={prev}>
              <Icon icon={faAngleLeft} />
            </IconButton>
            <Button
              onClick={setDateToToday}
              color="neutral"
              size={smDown ? 'small' : 'medium'}
            >
              {t('navigation.today')}
            </Button>
            <IconButton onClick={next}>
              <Icon icon={faAngleRight} />
            </IconButton>
          </Stack>
        </Box>
      )}
      <NewEventDialog
        open={!!newEventDialogDate}
        date={newEventDialogDate}
        trainerId={newEventTrainerId}
        availabilitySchedule={newEventAvailabilitySchedule}
        onClose={() => {
          setNewEventDialogDate(null);
          setNewEventTrainerId(undefined);
          setNewEventAvailabilitySchedule(null);
        }}
        onSaveSuccess={async (appointment) => {
          setNewEventDialogDate(null);
          setNewEventTrainerId(undefined);
          setNewEventAvailabilitySchedule(null);
          setEventInUrl(appointment.id, 'appointment');

          // If this is a recurring appointment, fetch the scheduled appointment
          // and members so SessionSummary can display recurring details immediately
          if (appointment.scheduledAppointmentId) {
            const [scheduledAppointment, membersResponse] = await Promise.all([
              fetchScheduledAppointment(appointment.scheduledAppointmentId),
              fetchScheduledMemberships({
                scheduledAppointmentId: appointment.scheduledAppointmentId,
                appointmentId: appointment.id,
                fetchAll: true,
              }),
            ]);
            setViewedAppointment({
              ...appointment,
              scheduledAppointment,
              scheduledMembers: membersResponse.data,
            });
          } else {
            setViewedAppointment(appointment);
          }
        }}
      />
      <ViewEventDialog
        open={!!viewedAppointment || isLoadingAppointment}
        appointment={viewedAppointment}
        isLoading={isLoadingAppointment}
        onClose={() => {
          setViewedAppointment(null);
          setIsLoadingAppointment(false);
          setEventInUrl(null, null);
          // Refresh calendar data after closing the dialog
          queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
          queryClient.invalidateQueries({ queryKey: ['calendar-schedules'] });
        }}
        onAppointmentUpdate={setViewedAppointment}
        onRefresh={async () => {
          // Refetch appointment, visits, and waitlist entries
          if (viewedAppointment?.id) {
            const [appointment, visitsResponse, entries] = await Promise.all([
              fetchAppointment(viewedAppointment.id),
              fetchVisits({
                sortKeys: ['status asc', 'user_first_name asc'],
                filters: {
                  appointmentId: viewedAppointment.id,
                  allVisits: true,
                },
                includeClients: true,
                perPage: 100,
              }),
              fetchAppointmentWaitListEntries(viewedAppointment.id),
            ]);

            // If it's a recurring session, also refetch scheduled appointment and members
            let scheduledAppointment: ScheduledAppointment | null | undefined =
              undefined;
            let scheduledMembers: ScheduledMembership[] | null | undefined =
              undefined;

            if (appointment.scheduledAppointmentId) {
              const [scheduledAppt, membersResponse] = await Promise.all([
                fetchScheduledAppointment(appointment.scheduledAppointmentId),
                fetchScheduledMemberships({
                  scheduledAppointmentId: appointment.scheduledAppointmentId,
                  appointmentId: appointment.id,
                  fetchAll: true,
                }),
              ]);
              scheduledAppointment = scheduledAppt;
              scheduledMembers = membersResponse.data;
            }

            setViewedAppointment({
              ...appointment,
              visits: visitsResponse.visits,
              entries,
              scheduledAppointment,
              scheduledMembers,
            });
          }
        }}
      />
      <UnavailableDialog
        open={!!viewedSchedule || isLoadingSchedule}
        schedule={viewedSchedule}
        onClose={() => {
          setViewedSchedule(null);
          setIsLoadingSchedule(false);
          setEventInUrl(null, null);
          // Refresh calendar data after closing the dialog
          queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
          queryClient.invalidateQueries({ queryKey: ['calendar-schedules'] });
        }}
      />
      <Dialog
        open={rescheduleDialogOpen}
        title={t('dragDrop.confirmTitle')}
        onClose={handleRescheduleCancel}
        actions={
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Button
              fullWidth
              onClick={handleRescheduleNotify}
              color="primary"
              variant="prominent"
            >
              {t('dragDrop.notifyAttendees')}
            </Button>
            <Button
              fullWidth
              onClick={handleRescheduleDontNotify}
              color="neutral"
            >
              {t('dragDrop.dontNotify')}
            </Button>
            <Button fullWidth onClick={handleRescheduleCancel} color="neutral">
              {t('dragDrop.cancel')}
            </Button>
          </Stack>
        }
      >
        <Typography sx={{ textAlign: 'center' }}>
          {t('dragDrop.confirmMessage')}
        </Typography>
      </Dialog>
    </>
  );
}
